import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MessageCircle } from "lucide-react";
import { HERO_IMG, HERO_ACCENT_IMG } from "@/data/content";
import { easeOutExpo } from "@/lib/motion";
import { scrollToId } from "@/lib/scroll";
import { waLink, DEFAULT_WA_MSG } from "@/lib/site";

const line = {
  hidden: { y: "112%" },
  show: (i) => ({
    y: 0,
    transition: { duration: 1.05, delay: 0.25 + i * 0.13, ease: easeOutExpo },
  }),
};

function Stamp() {
  return (
    <div className="absolute -top-7 -right-4 sm:-right-8 z-10">
      <div className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-full bg-ivory shadow-[0_18px_50px_-18px_rgba(26,21,18,0.45)] border border-gold/40 flex items-center justify-center">
        <svg viewBox="0 0 120 120" className="absolute inset-0 h-full w-full spin-slow">
          <defs>
            <path id="stamp-circ" d="M60,60 m-44,0 a44,44 0 1,1 88,0 a44,44 0 1,1 -88,0" />
          </defs>
          <text fontSize="10" letterSpacing="2.6" fill="#8B1E22" style={{ fontFamily: "JetBrains Mono, monospace" }}>
            <textPath href="#stamp-circ" letterSpacing="4.2">A TO Z GIFT AND CRAFT HOUSE</textPath>
          </text>
        </svg>
        <svg viewBox="0 0 48 48" className="h-8 w-8" fill="none">
          <rect x="10" y="21" width="28" height="17" rx="3" stroke="#8B1E22" strokeWidth="2.4" />
          <rect x="7" y="14.5" width="34" height="6.5" rx="2" fill="#D4AF37" />
          <path d="M24 14.5C21 8.5 14 7.5 15.5 12c.7 2.3 5 2.5 8.5 2.5z" fill="#C85A32" />
          <path d="M24 14.5C27 8.5 34 7.5 32.5 12c-.7 2.3-5 2.5-8.5 2.5z" fill="#D4AF37" />
        </svg>
      </div>
    </div>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yGlow = useTransform(scrollYProgress, [0, 1], [0, -60]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section id="top" ref={ref} className="relative overflow-hidden pt-[112px] pb-16 lg:pb-24" data-testid="hero-section">
      {/* atelier wash */}
      <motion.div style={{ y: yGlow }} className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div className="absolute -top-40 -left-40 h-[560px] w-[560px] rounded-full bg-terracotta/15 blur-[140px]" />
        <div className="absolute top-1/3 -right-40 h-[520px] w-[520px] rounded-full bg-gold/25 blur-[150px]" />
        <div className="absolute bottom-0 left-1/3 h-[380px] w-[380px] rounded-full bg-crimson/10 blur-[130px]" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1, ease: easeOutExpo }}
            className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-crimson"
            data-testid="hero-eyebrow"
          >
            Handcrafted Gifting Atelier · India
          </motion.p>

          <h1 className="mt-6 font-display font-semibold text-[44px] leading-[1.04] sm:text-6xl lg:text-7xl xl:text-[84px] tracking-tight text-charcoal">
            <span className="block overflow-hidden pb-1">
              <motion.span custom={0} variants={line} initial="hidden" animate="show" className="block" data-testid="hero-headline-line-1">
                Make Every Moment
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span custom={1} variants={line} initial="hidden" animate="show" className="block italic text-crimson" data-testid="hero-headline-line-2">
                Unforgettable
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span custom={2} variants={line} initial="hidden" animate="show" className="block" data-testid="hero-headline-line-3">
                — from A to Z.
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: easeOutExpo }}
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-clay"
            data-testid="hero-subline"
          >
            Personalized keepsakes, festive hampers, wedding favors and corporate
            gift boxes — curated, customized and hand-packed with love.
            Order in minutes, right on WhatsApp.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: easeOutExpo }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={() => scrollToId("#collections")}
              data-testid="hero-explore-collections-button"
              className="group inline-flex items-center gap-3 rounded-full bg-charcoal text-ivory px-7 py-4 text-sm font-semibold hover:bg-crimson transition-[background-color,transform] duration-300 hover:-translate-y-0.5"
            >
              Explore Occasion Collections
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
            <a
              href={waLink(DEFAULT_WA_MSG)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-inquire-whatsapp-button"
              className="inline-flex items-center gap-3 rounded-full border border-crimson/40 text-crimson px-7 py-4 text-sm font-semibold hover:bg-crimson hover:text-ivory transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp Inquiry
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-clay/80"
            data-testid="hero-trust-chips"
          >
            <span>Handmade in India</span>
            <span className="text-gold">✦</span>
            <span>Custom Orders Welcome</span>
            <span className="text-gold">✦</span>
            <span>Pan-India Delivery</span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative" onMouseMove={onMove} onMouseLeave={reset}>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: easeOutExpo }}
            style={{ rotateX, rotateY, transformPerspective: 1000 }}
            className="relative mx-auto max-w-[420px]"
            data-testid="hero-3d-showcase"
          >
            <motion.div style={{ y: yImg }} className="relative">
              <Stamp />
              <div className="arch overflow-hidden border border-gold/30 shadow-[0_50px_100px_-40px_rgba(26,21,18,0.5)]">
                <img
                  src={HERO_IMG}
                  alt="Handcrafted luxury gift box with handmade roses and a scented candle"
                  className="img-warm w-full h-[440px] sm:h-[540px] object-cover"
                  data-testid="hero-main-image"
                />
                <div className="absolute inset-0 bg-[radial-gradient(80%_60%_at_50%_0%,rgba(250,247,242,0.18),transparent_60%)]" />
              </div>
              <div className="arch absolute inset-0 border border-ivory/50 pointer-events-none" />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -30, y: 10 }}
              animate={{ opacity: 1, x: 0, y: 0 }}
              transition={{ duration: 1, delay: 1.05, ease: easeOutExpo }}
              className="float-soft absolute -bottom-8 -left-6 sm:-left-12 w-36 sm:w-44 rotate-[-5deg]"
            >
              <div className="rounded-2xl overflow-hidden border-4 border-ivory shadow-[0_30px_60px_-25px_rgba(26,21,18,0.55)]">
                <img
                  src={HERO_ACCENT_IMG}
                  alt="Gourmet chocolate and praline gift basket"
                  className="img-warm w-full h-32 sm:h-40 object-cover"
                  data-testid="hero-accent-image"
                />
              </div>
              <p className="mt-2 text-center font-mono text-[10px] uppercase tracking-[0.18em] text-clay" data-testid="hero-accent-caption">
                Hand-packed, always
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
